import {Button, Dialog, Stack, Typography} from '@mui/material';
import {useCallback, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {DT_FORMATS, formatDate} from 'utils';
import {CancelActionButtons} from '@ui/CancelActionButtons/CancelActionButtons';
import dayjs, {Dayjs} from 'dayjs';
import {FinalReportBlock} from '@ui/FinalReport/FinalReportBlock';
import {FinalReportFilters} from './FinalReportFilters';
import {ReportType} from './constants';
import {DownloadOutlined} from '@mui/icons-material';
import {Permission, usePermissions} from '../../utils/hooks/permissions/usePermissions';
import {useReportsManager} from '@ui/ReportModal/hooks/useReportsManager';
import {Warnings} from '@ui/ReportModal/Warnings';
import {useTranslation} from 'react-i18next';

type FinalReportDialogProps = {
    onClose: () => void;
};
export const FinalReportDialog = ({onClose}: FinalReportDialogProps) => {
    const [dateTo, setDateTo] = useState<Dayjs | null>(null);
    const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs());
    const params = useParams();
    const {roles} = usePermissions();
    const {startReport} = useReportsManager();

    const [selectedStages, setSelectedStages] = useState<string[]>([]);
    const [selectedCasingColumns, setSelectedCasingColumns] = useState<string[]>([]);
    const [reports, setReports] = useState<Set<ReportType>>(new Set());
    const [disabled, setDisabled] = useState(false);
    const [warnings, setWarnings] = useState<string[]>([]);

    const dailyReports = useMemo(
        () => new Set([ReportType.VMR, ReportType.DRILLING, ReportType.WELL_COMPLETION]),
        [],
    );
    const dailyReportSelected = useMemo(() => {
        return Array.from(reports).some((report) => dailyReports.has(report));
    }, [reports, dailyReports]);

    const updateSelectedReports = useCallback(
        (newValue: ReportType) => {
            const newReports = new Set(reports);

            if (newReports.has(newValue)) {
                newReports.delete(newValue);
            } else {
                if (dailyReports.has(newValue)) {
                    // если выбран суточный отчёт очищаем все выбранные отчёты.
                    newReports.clear();
                } else {
                    // если выбран не суточный удаляем из выбранных все суточные отчёты.
                    dailyReports.forEach((dr) => newReports.delete(dr));
                }
                newReports.add(newValue);
            }

            setReports(newReports);
        },
        [reports, dailyReports],
    );

    const isActionBtnDisabled =
        reports.size === 0 ||
        (reports.has(ReportType.VMR) && !dateFrom) ||
        (reports.has(ReportType.DRILLING) && !dateFrom) ||
        (reports.has(ReportType.WELL_COMPLETION) && !dateFrom) ||
        disabled;

    const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        let currentReportTypes = Array.from(reports);
        if (
            Array.from(reports).find(
                (item) =>
                    item === ReportType.NPV_WITHOUT_REPAIR ||
                    item === ReportType.NPV_REPAIR ||
                    item === ReportType.PNV ||
                    item === ReportType.ADDITIONAL_WORK,
            )
        ) {
            currentReportTypes = Array.from(reports).filter(
                (item) =>
                    item !== ReportType.NPV_WITHOUT_REPAIR &&
                    item !== ReportType.NPV_REPAIR &&
                    item !== ReportType.PNV &&
                    item !== ReportType.ADDITIONAL_WORK,
            );

            currentReportTypes[currentReportTypes.length] = ReportType.NPV;
        }

        e.preventDefault();

        const payload = {
            datesRange: {
                dateFrom: dateFrom ? formatDate(dateFrom, `${DT_FORMATS.utcDate}T00:00:00`) : null,
                dateTo: dateTo ? formatDate(dateTo, `${DT_FORMATS.utcDate}T00:00:00`) : null,
            },
            casingColumns: selectedCasingColumns,
            wellConstructionStages: selectedStages,
            wellId: params.wellId,
            reportTypes: currentReportTypes,
            incidentTypes: Array.from(reports).filter(
                (item) =>
                    item === ReportType.NPV_WITHOUT_REPAIR ||
                    item === ReportType.NPV_REPAIR ||
                    item === ReportType.PNV ||
                    item === ReportType.ADDITIONAL_WORK,
            ),
        };

        await startReport('report', {wellId: params.wellId}, payload);

        onClose();
    };

    const downloadFinalReport = async () => {
        await startReport('final-report', {}, {wellId: params.wellId});
        onClose();
    };

    const downloadGgdDeviationReport = async () => {
        await startReport('deviation-report', {}, {wellId: params.wellId});
        onClose();
    };

    const {t} = useTranslation(['supervising', 'common']);

    return (
        <Dialog
            open
            PaperProps={{
                sx: {
                    padding: '16px',
                    maxWidth: '700px',
                },
            }}
        >
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                marginBottom='16px'
            >
                <Typography variant='h4'>{`${t('download')} ${t('report').toLowerCase()}`}</Typography>
                {roles?.includes(Permission.CAN_ADMIN_ACTIONS) && (
                    <Button
                        variant='text'
                        color='primary'
                        startIcon={<DownloadOutlined />}
                        onClick={downloadGgdDeviationReport}
                    >
                        {`${t('finalReportDialog.deviationReport')} ${t('abbr.ggd')}`}
                    </Button>
                )}
                <Button
                    variant='text'
                    color='primary'
                    startIcon={<DownloadOutlined />}
                    onClick={downloadFinalReport}
                >
                    {t('finalReport')}
                </Button>
            </Stack>
            <FinalReportFilters
                dateFrom={dateFrom}
                dateTo={dateTo}
                selectedCasingColumns={selectedCasingColumns}
                selectedStages={selectedStages}
                setDateFrom={setDateFrom}
                setDateTo={setDateTo}
                setSelectedCasingColumns={setSelectedCasingColumns}
                setSelectedStages={setSelectedStages}
                setDisabled={setDisabled}
                setWarnings={setWarnings}
                dailyReportSelected={dailyReportSelected}
            />
            <form onSubmit={onFormSubmit}>
                <Stack gap='8px'>
                    <FinalReportBlock
                        title={t('finalReportDialog.dailyReports')}
                        reports={[ReportType.VMR, ReportType.DRILLING, ReportType.WELL_COMPLETION]}
                        allReports={reports}
                        updateSelectedReports={updateSelectedReports}
                    />
                    <FinalReportBlock
                        title={t('wellDesign')}
                        reports={[
                            ReportType.WELL_CONSTRUCTION,
                            ReportType.TRAJECTORY,
                            ReportType.MUD,
                            ReportType.CHISEL,
                            ReportType.BHA_RUN,
                        ]}
                        allReports={reports}
                        updateSelectedReports={updateSelectedReports}
                    />
                    <FinalReportBlock
                        title={t('finalReportDialog.materialConsumption')}
                        reports={[
                            ReportType.MTR,
                            ReportType.CHEMICAL_REAGENT_CONSUMPTION,
                            ReportType.TECHNIQUE,
                        ]}
                        allReports={reports}
                        updateSelectedReports={updateSelectedReports}
                    />
                    <FinalReportBlock
                        title={t('incidents')}
                        reports={[ReportType.NPV_WITHOUT_REPAIR, ReportType.NPV_REPAIR, ReportType.PNV, ReportType.ADDITIONAL_WORK]}
                        allReports={reports}
                        updateSelectedReports={updateSelectedReports}
                    />
                    <FinalReportBlock
                        title={t('instructions')}
                        reports={[ReportType.OT_PB]}
                        allReports={reports}
                        updateSelectedReports={updateSelectedReports}
                    />
                    <FinalReportBlock
                        title={t('finalReportDialog.constructionTimelineReport')}
                        reports={[ReportType.BKV, ReportType.GGD, ReportType.PIT]}
                        allReports={reports}
                        updateSelectedReports={updateSelectedReports}
                    />
                    {roles?.includes(Permission.CAN_DOWNLOAD_SUPERVISING_VIOLATIONS_REPORT) && (
                        <FinalReportBlock
                            title={t('supervisingViolations')}
                            reports={[ReportType.SUPERVISING_VIOLATIONS]}
                            allReports={reports}
                            updateSelectedReports={updateSelectedReports}
                        />
                    )}
                </Stack>
                <Warnings warnings={warnings} />
                <CancelActionButtons
                    handleClose={onClose}
                    isActionBtnDisabled={isActionBtnDisabled}
                />
            </form>
        </Dialog>
    );
};
